import React, { useMemo, useState, useEffect } from 'react';
import { useRouter, useSearch } from '@tanstack/react-router'
import useThrottledValue from '../hooks/useThrottledValue';
import datemathParser, { nowWithinAndShortRange } from '../utils/datemath';

export type TimeRange = [Date, Date, boolean]; // [from, to, live]


export type TimeInfo = {
  currentTime?: Date;
  timeRange: [Date, Date];
  playing: boolean;
  speedFactor: number;
  updating: boolean;  // are we polling for new data?
  live: boolean;  // is the "to" time near now? i.e. should we auto-advance currentTime to follow toTime=now?
};


const useTimeRange = (search: { from?: string; to?: string }) => {
  const router = useRouter();
  // const search = useSearch({ from: '/' as any }) as { from?: string; to?: string };
  const [dateUpdated, setDateUpdated] = React.useState<Date>(new Date());

  const normalizeRange = React.useCallback((from?: string, to?: string): [string, string] => {
    if (!from && !to) return ['now-1d', 'now'];
    if (!from && to) return [`${to}||-1d`.replace('now||', 'now'), to] as [string, string];
    if (from && !to) return [from, `${from}||+1d`.replace('now||', 'now')] as [string, string];
    return [from as string, to as string];
  }, []);

  const [timeRangeQuery, setTimeRangeQuery] = useState<[string, string]>(() =>
    normalizeRange(search.from, search.to)
  );

  const updatingLive = React.useMemo(() => nowWithinAndShortRange(timeRangeQuery[0], timeRangeQuery[1], 14 * 24 * 60 * 60 * 1000), [timeRangeQuery]);

  const timeRange = React.useMemo<TimeRange>(() => {
    const [qFromStr, qToStr] = timeRangeQuery;
    const now = dateUpdated;
    const timeZone = 'America/New_York' //Intl.DateTimeFormat().resolvedOptions().timeZone;
    const qFrom = datemathParser(qFromStr, { date: now, roundUp: false, timeZone });
    const qTo = datemathParser(qToStr, { date: now, roundUp: true, timeZone });
    const live = updatingLive; //qTo ? qTo.getTime() >= now.getTime() - (60 * 60 * 1000) && (qFrom?.getTime()||0) < now.getTime() : false; // within 1 hour of now
    return [qFrom, qTo, live];
    // if (qFrom && qTo) return [qFrom, qTo, live];
    // if (qFrom && !qTo) return [qFrom, new Date(qFrom.getTime() + 12 * 60 * 60 * 1000), live]; // +12h
    // if (!qFrom && qTo) return [new Date(qTo.getTime() - 12 * 60 * 60 * 1000), qTo, live]; // -12h
    // return [
    //   // 'now-1d', 'now', true,
    //   // new Date('2025-10-30T17:00:00Z'),
    //   // new Date('2025-10-31T03:00:00Z'),
    //   // false,
    // ];
  }, [timeRangeQuery, updatingLive, dateUpdated]);

  const setTimeRange = React.useCallback(
    (newTimeRangeQuery?: [string | Date, string | Date], replace?: boolean) => {
      if (!newTimeRangeQuery) return setDateUpdated(new Date());
      let [from, to] = newTimeRangeQuery;
      from = (from instanceof Date ? from.toISOString() : from) as string;
      to = (to instanceof Date ? to.toISOString() : to) as string;
      setTimeRangeQuery((prev) => {
        if (prev[0] === from && prev[1] === to) return prev;
        router.navigate({
          to: '.',
          search: (prevSearch: any) => ({ ...prevSearch, from, to }),
          replace,
        });
        return [from, to];
      });
      if (to === 'now') {
        setCurrentTime(true);
        setDateUpdated(new Date());
      } else {
        setCurrentTime((ct) => {
          const f = new Date(from);
          const t = new Date(to);
          if (ct && ct >= f && ct <= t) return ct;
          return undefined;
        });
      }
    },
    [router]
  );

  // Sync time range from browser history/search changes (back/forward)
  useEffect(() => {
    const next = normalizeRange(search.from, search.to);
    let changed = false;
    setTimeRangeQuery((prev) => {
      if (prev[0] === next[0] && prev[1] === next[1]) return prev;
      changed = true;
      return next;
    });
    if (!changed) return;
    setDateUpdated(new Date());
    if (next[1] === 'now') {
      setCurrentTime(true);
    } else {
      setCurrentTime((ct) => {
        const f = new Date(next[0]);
        const t = new Date(next[1]);
        if (ct && ct >= f && ct <= t) return ct;
        return undefined;
      });
    }
  }, [normalizeRange, search.from, search.to]);

  const [currentTime_, setCurrentTime] = React.useState<Date | true | undefined>(() => updatingLive ? true : undefined);//timeRange[0]
  const followingLive = currentTime_ === true;
  const currentTime = currentTime_ === true ? timeRange[1] : currentTime_;

  // console.log({
  //   timeRange, 
  //   timeRangeQuery,
  //   currentTime,
  //   currentTime_,
  //   updatingLive,
  //   followingLive,
  // })

  // Minute-aligned refresh when live
  React.useEffect(() => {
    if (!updatingLive) return;
    let timer: number;
    const schedule = () => {
      const now = new Date();
      const delay = 60000 - (now.getSeconds() * 1000 + now.getMilliseconds());
      timer = setTimeout(() => {
        console.log('Auto-updating live time range', now.toISOString());
        setTimeRange();
        schedule();
      }, delay < 10000 ? delay + 60000 : delay); // avoid too-soon updates
    };
    schedule();
    return () => clearTimeout(timer);
  }, [updatingLive, setTimeRange]);


  // Throttled currentTime for heavier map updates (update at most once per second)
  const throttledCurrentTime = useThrottledValue(currentTime, 1000);
  const currentDateStartOfDayMs = useMemo(() => {
    if (!currentTime) return;
    const d = new Date(currentTime);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, [currentTime]);
  const currentDateStartOfDay = useMemo(() => currentDateStartOfDayMs ? new Date(currentDateStartOfDayMs) : undefined, [currentDateStartOfDayMs]);

  const throttledCurrentDateStartOfDay = useMemo(() => {
    if (!throttledCurrentTime) return;
    const d = new Date(throttledCurrentTime);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [throttledCurrentTime]);

  return {
    timeRange, 
    timeRangeQuery,
    setTimeRange, 

    currentTime,
    setCurrentTime,

    updatingLive,
    followingLive,

    throttledCurrentTime,
    currentDateStartOfDay,
    throttledCurrentDateStartOfDay,
  };
};

export default useTimeRange;